/**
 * A pillar card (and the pillar page hero) paints white text and white-tinted chrome over the
 * pillar's own icon gradient. Most of those gradients are saturated and dark, and the prototype
 * shows them raw -- dimming them with a black scrim is exactly what made the ported cards look
 * washed out next to the reference. A handful of bank gradients are pale though (mint, peach,
 * pastel blue), and white on those is unreadable, so the scrim is applied to those only.
 */

import { buildGradientFromColor } from '@/utils/colorUtils';

/** Glyph a pillar falls back to when it carries no icon of its own. */
export const DEFAULT_PILLAR_ICON = 'target';

const HEX_PATTERN = /#([0-9a-f]{3}|[0-9a-f]{6})\b/i;

/** Above this WCAG relative luminance, white text on the colour stops being readable. */
const LIGHT_SURFACE_LUMINANCE = 0.45;

const expandShortHex = (hex: string): string =>
    hex.length === 3 ? `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}` : hex;

/** sRGB channel (0-255) to its linear-light value, per WCAG 2.1 relative luminance. */
const toLinearChannel = (channel: number): number => {
    const ratio = channel / 255;

    return ratio <= 0.03928 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4;
};

/**
 * WCAG relative luminance of the first colour stop of a CSS background value, or `undefined`
 * when the value carries no hex colour we can read (a named colour, an `rgb()` stop, an image).
 */
export const backgroundLuminance = (background: string): number | undefined => {
    const match = HEX_PATTERN.exec(background);
    if (!match) {
        return undefined;
    }

    const hex = expandShortHex(match[1]);
    const red = toLinearChannel(parseInt(hex.slice(0, 2), 16));
    const green = toLinearChannel(parseInt(hex.slice(2, 4), 16));
    const blue = toLinearChannel(parseInt(hex.slice(4, 6), 16));

    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

/**
 * Whether a pillar surface needs the dark readability scrim behind its white content. Dark and
 * mid-tone gradients keep their raw colour (the prototype's look); only pale ones get the scrim.
 * An unreadable background is treated as dark: better a vivid card than a needlessly dimmed one.
 */
export const needsReadabilityOverlay = (background: string): boolean => {
    const luminance = backgroundLuminance(background);

    return luminance !== undefined && luminance > LIGHT_SURFACE_LUMINANCE;
};

/**
 * The reference design keeps a pillar tile in its OWN
 * hue: barely lighter at the near corner, sharply darker at the far one -- deep and saturated,
 * never crossing into another colour. Our icon-bank defaults and older admin picks predate that
 * recipe and cross hues diagonally instead (pink -> yellow), which reads washed out next to the
 * reference. This re-derives any pillar background -- a plain hex, or a
 * legacy two-hue gradient string -- from its first colour stop, through the exact recipe
 * `colorUtils.buildGradientFromColor` already applies to a freshly picked admin colour.
 */
export const derivePillarTileGradient = (background: string): string => {
    const match = HEX_PATTERN.exec(background);
    if (!match) {
        return background;
    }

    return buildGradientFromColor(`#${expandShortHex(match[1])}`);
};
